import { use, useEffect, useRef, useState } from 'react';
import { delay } from 'msw';
import frontEndMessageHub, { IFrontEndMessageHub } from '@utils-clean/front-end-message-hub';
import { UserContext } from '@contexts/user-context';

export interface MessageHubHookProps {
    messageHubRelativeEndpoint: string;
    entityType: string;
    parentId: string | undefined;
    onCommentsUpdated: (commentIds: string[]) => Promise<boolean>;
    onCommentDeleted: (isSelfMessage: boolean, commentId: string) => void;
}

export interface IMessageHubHook {
    connectedAtLeastOnce: boolean;
    disconnect: () => void;
    startProcessingUpdates: () => void;
}

const useCommentsMessageHub = ({
    messageHubRelativeEndpoint,
    parentId,
    onCommentsUpdated,
    onCommentDeleted,
}: MessageHubHookProps): IMessageHubHook => {
    const { user } = use(UserContext);

    // Fixed for the lifetime of the component. Changing this requires remounting the comments sidebar
    const messageHubRef = useRef<IFrontEndMessageHub>(undefined);
    if (!messageHubRef.current) {
        messageHubRef.current = frontEndMessageHub(messageHubRelativeEndpoint);
    }
    const { hubConnection, connectToHub, disconnectFromHub } = messageHubRef.current;

    const connectRetryAbortControllerRef = useRef<AbortController>(new AbortController());

    const [connectedAtLeastOnce, setConnectedAtLeastOnce] = useState(false);
    const updateQueueRef = useRef<Set<string>>(new Set());
    const updatesPausedRef = useRef<boolean>(true);
    const processingUpdatesRef = useRef<boolean>(false);

    const processQueuedUpdatesRecursive = async () => {
        if (updatesPausedRef.current || !(updateQueueRef.current.size > 0)) {
            return;
        }

        const commentIds = [...updateQueueRef.current];
        updateQueueRef.current.clear();

        const success = await onCommentsUpdated(commentIds);

        if (!success) {
            updateQueueRef.current = new Set([...commentIds, ...updateQueueRef.current]);
            await delay(5000);
        }

        await processQueuedUpdatesRecursive();
    };

    const processQueuedUpdates = async () => {
        if (processingUpdatesRef.current) {
            return;
        }

        processingUpdatesRef.current = true;
        await processQueuedUpdatesRecursive();
        processingUpdatesRef.current = false;
    };

    const startProcessingUpdates = () => {
        updatesPausedRef.current = false;

        // eslint-disable-next-line no-void
        void processQueuedUpdates();
    };

    const connectEndlessRetry = async (abortController: AbortController) => {
        if (abortController.signal.aborted) {
            return;
        }

        try {
            await connectToHub(parentId!);
            if (abortController.signal.aborted) {
                disconnectFromHub();
            }
            setConnectedAtLeastOnce(true);
        } catch {
            await delay(5000);
            await connectEndlessRetry(abortController);
        }
    };

    const disconnect = () => {
        hubConnection.off('CommentUpdated');
        hubConnection.off('CommentDeleted');
        updatesPausedRef.current = true;
        connectRetryAbortControllerRef.current.abort();
        disconnectFromHub();
    };

    useEffect(() => {
        if (parentId) {
            hubConnection.on('CommentUpdated', (data) => {
                updateQueueRef.current.add(data.id);
                // eslint-disable-next-line no-void
                void processQueuedUpdates();
            });

            hubConnection.on('CommentDeleted', (data) => {
                onCommentDeleted(user?.id === data.publisherUserId, data.id);
            });

            // eslint-disable-next-line no-void
            void connectEndlessRetry(connectRetryAbortControllerRef.current);

            hubConnection.onreconnecting(() => connectRetryAbortControllerRef.current.abort());

            hubConnection.onreconnected(() => {
                connectRetryAbortControllerRef.current.abort();
                connectRetryAbortControllerRef.current = new AbortController();
                // eslint-disable-next-line no-void
                void connectEndlessRetry(connectRetryAbortControllerRef.current);
            });
        }

        return () => {
            disconnect();
        };
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [parentId]);

    return { connectedAtLeastOnce, disconnect, startProcessingUpdates };
};

export default useCommentsMessageHub;
